// 👥 Employés E4b — Section 4 : Formations (clé RBAC `formations`). Ajout/retrait inline, certificat joint (data URL).
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, GraduationCap, Plus, Trash2 } from 'lucide-react';
import { useEditionSection } from '@/hooks/useEditionSection';
import { SectionFiche } from './SectionFiche';

export const CHAMPS_FORMATIONS = ['formations'];
const VIDE = { nom_formation: '', date_formation: '', date_expiration: '', certificat_url: null, notes: '' };

export function SectionFormations({ employee, isProprietaire }) {
  const ed = useEditionSection('formations', CHAMPS_FORMATIONS);
  const [nouvelle, setNouvelle] = useState(VIDE);
  const liste = (ed.enEdition ? ed.brouillon.formations : employee.formations) || [];
  const expiree = (f) => f.date_expiration && new Date(f.date_expiration) < new Date();

  const ajouter = () => {
    if (!nouvelle.nom_formation || !nouvelle.date_formation) return;
    ed.changer('formations', [...liste, { ...nouvelle, id: Date.now().toString() }]);
    setNouvelle(VIDE);
  };
  const joindre = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setNouvelle((n) => ({ ...n, certificat_url: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <SectionFiche cle="formations" titre="Formations reçues" icone={GraduationCap} peutModifier={isProprietaire} edition={ed}>
      {liste.length === 0 && <p className="text-sm text-slate-400 text-center py-4" data-testid="formations-vide">Aucune formation enregistrée</p>}
      <div className="space-y-2">
        {liste.map((f, i) => (
          <div key={f.id || f._id || i} className="p-3 border rounded-lg flex justify-between items-start gap-3" data-testid={`formation-${i}`}>
            <div className="min-w-0">
              <p className="font-semibold text-sm">{f.nom_formation}</p>
              <p className="text-xs text-slate-500">Complétée le {f.date_formation}</p>
              {f.notes && <p className="text-xs text-slate-600 mt-1">{f.notes}</p>}
              {f.certificat_url && <a href={f.certificat_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 flex items-center gap-1 mt-1" data-testid={`formation-${i}-certificat`}><FileText className="h-3 w-3" />Certificat joint</a>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {f.date_expiration && <Badge className={expiree(f) ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>Expire : {f.date_expiration}</Badge>}
              {ed.enEdition && <button type="button" onClick={() => ed.changer('formations', liste.filter((_, j) => j !== i))} className="text-red-600" data-testid={`retirer-formation-${i}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>
        ))}
      </div>
      {ed.enEdition && (
        <div className="mt-4 border-t pt-3 space-y-2" data-testid="formation-ajout">
          <Input placeholder="Nom de la formation (ex. Santé et sécurité)" value={nouvelle.nom_formation} onChange={(e) => setNouvelle({ ...nouvelle, nom_formation: e.target.value })} className="h-8" data-testid="input-formation-nom" />
          <div className="grid grid-cols-2 gap-2">
            <div><p className="text-xs text-slate-500">Date de formation</p><Input type="date" value={nouvelle.date_formation} onChange={(e) => setNouvelle({ ...nouvelle, date_formation: e.target.value })} className="h-8" data-testid="input-formation-date" /></div>
            <div><p className="text-xs text-slate-500">Expiration (optionnel)</p><Input type="date" value={nouvelle.date_expiration} onChange={(e) => setNouvelle({ ...nouvelle, date_expiration: e.target.value })} className="h-8" data-testid="input-formation-expiration" /></div>
          </div>
          <Textarea rows={2} placeholder="Notes" value={nouvelle.notes} onChange={(e) => setNouvelle({ ...nouvelle, notes: e.target.value })} data-testid="input-formation-notes" />
          <div className="flex items-center justify-between gap-2">
            <label className="text-xs text-slate-600 flex items-center gap-2">Certificat : <input type="file" accept="image/*,.pdf" onChange={(e) => joindre(e.target.files?.[0])} data-testid="input-formation-certificat" />{nouvelle.certificat_url && <span className="text-emerald-700">joint</span>}</label>
            <Button size="sm" variant="outline" disabled={!nouvelle.nom_formation || !nouvelle.date_formation} onClick={ajouter} data-testid="btn-ajouter-formation"><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter la formation</Button>
          </div>
        </div>
      )}
    </SectionFiche>
  );
}
